// What the Game Day Lab shows about a mock game, worked out from the fields the backend's mock
// driver writes (cron/game/updateLiveGamesMock). Pure, so the page and its tests read one answer.

/** The jump buttons, in match order. `minute` is what the backend's `minute` field takes. */
export const JUMPS = [
  { key: 'pre', label: 'Pre-match', hint: 'Kicks off in 3 min', phase: 'pre_match', minute: 3 },
  { key: 'ko', label: 'Kick-off', hint: "1'", phase: 'first_half', minute: 0 },
  { key: '1h', label: "1H 30'", hint: 'First half', phase: 'first_half', minute: 30 },
  { key: '1hst', label: "45+'", hint: '1H stoppage', phase: 'first_half', minute: 45 },
  { key: 'ht', label: 'Half-time', hint: '16 min break', phase: 'half_time' },
  { key: '2h', label: "2H 60'", hint: 'Second half', phase: 'second_half', minute: 60 },
  { key: '2hend', label: "2H 88'", hint: 'Closing minutes', phase: 'second_half', minute: 88 },
  { key: 'pen', label: 'Penalties', hint: 'J1, level score', phase: 'penalties' },
  { key: 'ft', label: 'Full time', hint: 'Final whistle', phase: 'full_time' }
];

/** A match minute typed into the custom box, as the backend's phase + minute. */
export function jumpForMinute(minute) {
  if (minute === '' || minute == null) return null;
  const m = Math.round(Number(minute));
  if (!Number.isFinite(m) || m < 0 || m > 90) return null;
  return m <= 45 ? { phase: 'first_half', minute: m } : { phase: 'second_half', minute: m };
}

const PHASE_LABELS = {
  '1H': 'First half',
  HT: 'Half-time',
  '2H': 'Second half',
  P: 'Penalties',
  PEN: 'Finished on penalties',
  FT: 'Full time'
};

/** One label for where the match is, from the driver's status pair. */
export function phaseOf(game) {
  if (!game) return { label: '—', tone: 'idle' };
  if (game.status === 'finished') {
    return { label: PHASE_LABELS[game.gameStatus] || 'Finished', tone: 'done' };
  }
  if (!game.gameStatus) return { label: 'Pre-match', tone: 'idle' };
  return { label: PHASE_LABELS[game.gameStatus] || game.gameStatus, tone: 'live' };
}

/** The match clock as the app shows it: 67', 45+2', or nothing outside play. */
export function clockOf(game) {
  if (!game || !['1H', '2H'].includes(game.gameStatus) || game.progress == null) return '';
  return game.extraTime ? `${game.progress}+${game.extraTime}'` : `${game.progress}'`;
}

/** "Kicks off in 3 min" / "kicked off 12 min ago", against the server's clock. */
export function kickoffText(game, serverNow) {
  if (!game?.date) return '';
  const minutes = Math.round((new Date(game.date) - new Date(serverNow || Date.now())) / 60000);
  if (minutes > 0) return `Kicks off in ${minutes} min`;
  if (minutes === 0) return 'Kicks off now';
  return `Kicked off ${-minutes} min ago`;
}

/** Goals asked for that the driver has not scored yet. */
export function queuedGoals(game) {
  const pending = game?.mockConfig?.pendingGoals || {};
  return { home: Math.max(0, pending.home || 0), away: Math.max(0, pending.away || 0) };
}

/** Bodies differ by endpoint: some send `{ data }`, some send the payload flat. */
export const unwrap = (res) =>
  res && typeof res === 'object' && res.data !== undefined && res.state === undefined
    ? res.data
    : res;

export const GOAL_CHANCES = [
  { value: 'Off', chance: 0 },
  { value: 'Low (5%)', chance: 0.05 },
  { value: 'Normal (15%)', chance: 0.15 },
  { value: 'High (50%)', chance: 0.5 }
];

export const SCORING_TEAMS = [
  { value: 'Either side', key: 'random' },
  { value: 'Home only', key: 'home' },
  { value: 'Away only', key: 'away' }
];

/** Wall-clock time of a timeline stamp, or a dash. */
export const timeOf = (value) =>
  value
    ? new Date(value).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    : '—';

/** Minutes from now as a phrase: "in 12 min", "3 min ago", "now". */
export function relative(value, now = Date.now()) {
  if (!value) return '';
  const minutes = Math.round((new Date(value) - new Date(now)) / 60000);
  if (minutes === 0) return 'now';
  return minutes > 0 ? `in ${minutes} min` : `${-minutes} min ago`;
}

/** "18:47 (in 12 min)" — a clock time with how far off it is. */
export const when = (value, now) =>
  value
    ? `${new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} (${relative(value, now)})`
    : '';

// Plain-language states. Each says what a fan can do, which is what QA checks against the app.
export const POLL_STATES = {
  open: { label: 'Open', detail: 'Fans can vote', tone: 'live' },
  closed: { label: 'Closed', detail: 'Fans see the results', tone: 'done' },
  hidden: { label: 'Hidden', detail: 'Not shown — inactive, or not game day', tone: 'idle' }
};

export const QUIZ_STATES = {
  ready_to_play: { label: 'Open', detail: 'Fans can answer', tone: 'live' },
  coming_up: { label: 'Waiting', detail: 'Opens at the half-time whistle', tone: 'info' },
  locked: { label: 'Closed', detail: 'No longer taking answers', tone: 'done' },
  completed: { label: 'Answered', detail: 'Already answered', tone: 'done' }
};

export const FANWAVE_STATES = {
  coming_up: { label: 'Coming up', detail: 'Visible to fans, taps not counted yet', tone: 'info' },
  ready_to_play: { label: 'Open', detail: 'Fans can tap', tone: 'live' },
  completed: { label: 'Finished', detail: 'Window over — you took part', tone: 'done' },
  locked: {
    label: 'Finished',
    detail: 'Window over or locked — you did not take part',
    tone: 'done'
  },
  no_fanwave: { label: 'No wave', detail: 'Nothing set up for this match', tone: 'idle' }
};

/** The Team Channel's rules, as the admin orders them in General Settings. */
export const CLAIM_LABELS = {
  poll_open: 'Poll is open for votes',
  quiz_answerable: 'Quiz is taking answers',
  quiz_displayable: 'Quiz is still on display',
  poll_closed: 'Poll shows its results'
};

/** Which rule gave the slot to what it shows, from the module and that module's state. */
export function winningClaim(surface) {
  if (!surface || surface.surface === 'none') return null;
  if (surface.surface === 'poll') {
    return surface.pollState === 'open' ? 'poll_open' : 'poll_closed';
  }
  return surface.quizState === 'ready_to_play' ? 'quiz_answerable' : 'quiz_displayable';
}
